import React, { useEffect } from 'react';
import NavBar from '../../components/NavBar';
import ResponsiveFooter from '../../components/ResponsiveFooter';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

// Apple-inspired color palette (matching LandingPage.jsx)
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray600: '#6e6e6e',
  gray700: '#636363',
  gray900: '#333333',
};

// Global font styles (matching LandingPage.jsx)
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    color: '#000000',
  },
  subheading: {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    color: '#333333',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
    color: '#333333',
  },
  medium: {
    fontWeight: 500,
  }
};

// Animation variants
const fadeInVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.6,
      ease: [0.6, -0.05, 0.01, 0.99]
    } 
  }
};

const VisionPage = () => {

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col"
      style={{
        color: colors.gray900,
        fontFamily: fontStyles.fontFamily
      }}>
      {/* Navigation */}
      <NavBar 
        activePage="vision"
      />
      
      {/* Content */}
      <motion.div 
        className="relative py-20 overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={fadeInVariants}
        style={{
          fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
          background: 'linear-gradient(to bottom, white, #f8faff)'
        }}
      >
        {/* Vertical Rails - Aligned with Footer positioning */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
          <div className="max-w-7xl mx-auto h-full relative" style={{ paddingLeft: 24, paddingRight: 24 }}>
            {/* Left vertical lines - slightly darker */}
            <div className="absolute left-4 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.18)',
                opacity: 0.8
              }} 
            />
            <div className="absolute left-8 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.14)',
                opacity: 0.9
              }} 
            />
            {/* Right vertical lines - slightly darker */}
            <div className="absolute right-4 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.18)',
                opacity: 0.8
              }} 
            />
            <div className="absolute right-8 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.14)',
                opacity: 0.9
              }} 
            />
          </div>
        </div>
        
        <div className="container mx-auto px-6 pt-12 pb-0 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Back link */}
          <Link to="/" className="inline-flex items-center text-sm transition-colors mb-10" 
            style={{color: colors.gray600}}
            onMouseOver={(e) => {e.currentTarget.style.color = colors.accent}}
            onMouseOut={(e) => {e.currentTarget.style.color = colors.gray600}}
          >
            <ArrowLeft size={14} className="mr-1" />
            Back to Home
          </Link>
          
          <div className="prose prose-lg max-w-none">
            <div className="flex flex-col items-start mb-6">
              <img src="/6.png" alt="SignPact Logo" className="h-12 mb-4" />
              <h1 className="text-4xl font-medium tracking-normal" 
                style={{
                  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontWeight: 500,
                  color: '#000000',
                  letterSpacing: '-0.02em',
                  marginTop: '0.25rem'
                }}>
                Vision.
              </h1>
            </div>
            
            <p className="text-sm mb-14" style={{color: colors.gray500}}>
              Last Updated: June 12, 2025
            </p>
            
            <p className="mb-6" style={fontStyles.body}>
            SignPact cuts through contract chaos. With a clean, intuitive interface and AI‑powered insights, you move from draft to signed deal in seconds—no confusing redlines, endless email chains, or hidden risks. We give teams the confidence to trust faster, negotiate smarter, and get back to building the future.
            </p>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              1. Our Mission
            </h2>
            
            <p className="mb-6" style={fontStyles.body}>
              SignPact's mission is to revolutionize how businesses handle contracts through the strategic integration of artificial intelligence, user-centered design, and robust security. We aim to create a platform that not only streamlines the contract lifecycle but also provides meaningful insights that help businesses make better decisions and reduce risks.
            </p>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              2. Core Principles
            </h2>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Simplicity Through Intelligence:</strong> We believe technology should simplify complex processes, not add complexity. Our AI-powered features are designed to handle the complicated aspects of contract management, presenting users with clear, actionable information.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Security as Foundation:</strong> Trust is non-negotiable in document management. We implement bank-level security measures and maintain complete transparency about how we protect your data.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Accessibility for All:</strong> We believe powerful contract management tools should be accessible to businesses of all sizes, not just enterprises with large budgets. Our pricing structure and feature set are designed to scale from solo entrepreneurs to multinational corporations.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Continuous Innovation:</strong> The legal and business landscapes are constantly evolving, and so must our platform. We commit to continuous improvement and innovation based on user feedback and emerging technologies.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              3. The Problem We're Solving
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              Contracts are the foundation of business relationships, yet they remain one of the most inefficient aspects of modern business:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Time Sink:</strong> Businesses spend an average of 20-30 hours per month on contract-related tasks, with legal review often creating bottlenecks.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Risk Exposure:</strong> Overlooked clauses, missed renewal dates, and human error in contract review can lead to significant financial and legal risks.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Fragmented Solutions:</strong> Many businesses use different tools for drafting, signing, storing, and analyzing contracts, creating inefficiencies and information silos.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Limited Intelligence:</strong> Traditional contract tools lack the ability to provide meaningful insights or proactively identify potential issues.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              4. Our Solution: The Three Pillars
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              SignPact addresses these challenges through an integrated platform built on three core pillars:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>E-Signature Excellence:</strong> Secure, legally-binding electronic signatures that work across devices and comply with regulations worldwide, including ESIGN, UETA, and eIDAS.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Complete Contract Lifecycle Management:</strong> From drafting and negotiation to execution, storage, and renewal, SignPact provides a centralized hub for all contract-related activities with automated workflows and reminders.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>AI-Powered Insights:</strong> Our proprietary AI analyzes contracts to identify risks, suggest improvements, extract key terms, and provide actionable business intelligence based on your entire contract portfolio.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              5. Our Long-Term Vision
            </h2>
            
            <p className="mb-6" style={fontStyles.body}>
              Beyond our current offerings, we envision SignPact evolving into:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>A Contract Intelligence Platform:</strong> Moving beyond management to provide predictive analytics that help businesses optimize their contractual relationships and identify new opportunities.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>An Industry Standard:</strong> Becoming the default solution for businesses of all sizes when they think about contract management and e-signatures.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>A Force for Standardization:</strong> Contributing to industry-wide improvements in contract clarity, fairness, and efficiency through our AI tools and templates.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              6. Commitment to Our Users
            </h2>
            
            <p className="mb-6" style={fontStyles.body}>
              As we pursue this vision, we make these commitments to our users:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Ethical AI Development:</strong> Our AI capabilities will always be developed with ethics in mind, avoiding bias and maintaining human oversight.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Data Stewardship:</strong> We will always be transparent about how we use your data and will never sell or misuse your information.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>User-Driven Evolution:</strong> Our roadmap will be shaped by user feedback and real-world needs, not just technological possibilities.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Inclusive Design:</strong> We will maintain accessibility and usability for users of all technical skill levels.
              </li>
            </ul>
            
            {/* Concluding section */}
            <div className="border-t my-12 pt-12" style={{borderColor: colors.gray200}}>
              <h2 className="text-2xl mb-4" 
                style={fontStyles.heading}>
                Join Us On This Journey
              </h2>
              <p className="mb-6" style={fontStyles.body}>
                SignPact is more than just software; it's a vision for how businesses can operate more efficiently, intelligently, and confidently in an increasingly complex world. We invite you to join us on this journey toward transforming contract management from a necessary burden into a strategic advantage.
              </p>
              
              <p className="mb-4" style={fontStyles.body}>
                Whether you're a small business owner overwhelmed by paperwork, a legal professional seeking more efficient ways to serve clients, or an enterprise looking to modernize your contract processes, SignPact is designed with your needs in mind.
              </p>
              
              <p style={fontStyles.body}>
                Together, we can reimagine what contract management can be and build a future where digital agreements are seamless, intelligent, and truly empowering.
              </p>
            </div>
          </div>
        </div>
      </div>
      </motion.div>
      
      {/* Section Divider - Rail-style horizontal lines */}
      <div className="relative w-full">
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
        {/* Gap between double rails */}
        <div 
          className="w-full"
          style={{
            height: '8px',
            background: 'transparent'
          }}
        />
        {/* Double rail effect - second line */}
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
      </div>
      
      {/* Footer */}
      <ResponsiveFooter activePage="vision" />
    </div>
  );
};

export default VisionPage; 